// routeConflictCheck — TFR route-conflict detection without external deps.
//
// Given a flight plan route (array of [lon, lat] coordinate pairs) and a
// cruise altitude in feet MSL, checks each active TFR polygon for
// geometric intersection with the route.
//
// Algorithm:
//   1. For each TFR GeoJSON feature, extract altitude range from WFS
//      properties (LOW_ALTITUDE / HI_ALTITUDE), filtering features whose
//      altitude block doesn't cover the cruise altitude. If altitude
//      properties are absent we conservatively include the TFR.
//   2. Test every route segment [A,B] against the TFR polygon using:
//      a. Point-in-polygon check (does A or B lie inside the polygon?)
//      b. Segment vs. polygon-edge intersection (does the segment cross
//         any polygon edge?)
//
// Coordinates are treated as flat 2D (lon,lat) which introduces sub-NM
// error for short polygon edges near the equator — acceptable for airspace
// conflict detection purposes where TFRs are typically many miles across.
//
// Cross-checked against an independent manual geometry verification
// for the test cases in routeConflictCheck.test.ts.

export interface TfrConflict {
  /** NOTAM key from WFS (e.g. "6/0092"). */
  notamId: string;
  /** Altitude range string, e.g. "SFC – 17,999 ft MSL". */
  altitudeRange: string;
  /** Human-readable description or empty string. */
  description: string;
  /** Approximate waypoint index (0-based) where the route enters the TFR. */
  entryLegIndex: number;
  /** FAA TFR list page URL for operator reference. */
  listUrl: string;
}

// ---------------------------------------------------------------------------
// Flat-earth 2D geometry helpers (lon/lat treated as Cartesian)
// ---------------------------------------------------------------------------

/** True if the axis-aligned bounding box [minX,minY,maxX,maxY] overlaps
 *  the point (px,py) with a small epsilon guard. */
function bboxContains(
  minX: number, minY: number, maxX: number, maxY: number,
  px: number, py: number,
): boolean {
  return px >= minX && px <= maxX && py >= minY && py <= maxY;
}

/**
 * Ray-casting point-in-polygon.  Ray shoots in the +X direction from (px,py).
 * Returns true when the point is inside the ring. Works for simple polygons;
 * vertex-exactly-on-ray is handled via the y1 <= py < y2 convention.
 */
function pointInRing(px: number, py: number, ring: number[][]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * True if segment AB intersects segment CD (proper crossing or shared
 * endpoint). Uses the cross-product orientation test.
 */
function segmentsIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  const cross2d = (
    ox: number, oy: number,
    ux: number, uy: number,
    vx: number, vy: number,
  ) => (ux - ox) * (vy - oy) - (uy - oy) * (vx - ox);

  const d1 = cross2d(cx, cy, dx, dy, ax, ay);
  const d2 = cross2d(cx, cy, dx, dy, bx, by);
  const d3 = cross2d(ax, ay, bx, by, cx, cy);
  const d4 = cross2d(ax, ay, bx, by, dx, dy);

  if (d1 * d2 < 0 && d3 * d4 < 0) return true;

  // Collinear / endpoint cases: check if any endpoint lies on the
  // other segment using bbox containment.
  if (d1 === 0 && bboxContains(Math.min(cx,dx),Math.min(cy,dy),Math.max(cx,dx),Math.max(cy,dy),ax,ay)) return true;
  if (d2 === 0 && bboxContains(Math.min(cx,dx),Math.min(cy,dy),Math.max(cx,dx),Math.max(cy,dy),bx,by)) return true;
  if (d3 === 0 && bboxContains(Math.min(ax,bx),Math.min(ay,by),Math.max(ax,bx),Math.max(ay,by),cx,cy)) return true;
  if (d4 === 0 && bboxContains(Math.min(ax,bx),Math.min(ay,by),Math.max(ax,bx),Math.max(ay,by),dx,dy)) return true;

  return false;
}

/** True if segment (ax,ay)→(bx,by) intersects or is contained in the
 *  simple polygon ring (outer boundary, no holes). */
function segmentIntersectsRing(
  ax: number, ay: number, bx: number, by: number,
  ring: number[][],
): boolean {
  // Either endpoint inside the ring?
  if (pointInRing(ax, ay, ring)) return true;
  if (pointInRing(bx, by, ring)) return true;
  // Check every polygon edge.
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    if (
      segmentsIntersect(
        ax, ay, bx, by,
        ring[i][0], ring[i][1], ring[j][0], ring[j][1],
      )
    ) return true;
  }
  return false;
}

/** Test one route segment against a GeoJSON Polygon (may have holes; we
 *  test the outer ring only for the purposes of conflict detection). */
function segmentIntersectsPolygon(
  ax: number, ay: number, bx: number, by: number,
  polygon: GeoJSON.Polygon,
): boolean {
  const outer = polygon.coordinates[0] as number[][];
  return segmentIntersectsRing(ax, ay, bx, by, outer);
}

/** Test one route segment against a GeoJSON MultiPolygon. */
function segmentIntersectsMultiPolygon(
  ax: number, ay: number, bx: number, by: number,
  mp: GeoJSON.MultiPolygon,
): boolean {
  for (const polyCoords of mp.coordinates) {
    const outer = polyCoords[0] as number[][];
    if (segmentIntersectsRing(ax, ay, bx, by, outer)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Altitude helpers
// ---------------------------------------------------------------------------

/**
 * Parse FAA WFS altitude field.  The field may be a number (feet MSL),
 * the string "SFC" (0 ft), or null/undefined. Returns Infinity for
 * ceiling when the field is absent (conservative: include the TFR).
 */
function parseAltFt(raw: unknown, fallback: number): number {
  if (raw == null) return fallback;
  if (typeof raw === 'number') return raw;
  const s = String(raw).trim().toUpperCase();
  if (s === 'SFC' || s === '0' || s === '') return 0;
  // Some WFS responses carry strings like "10000" or "FL180" (=18000).
  if (s.startsWith('FL')) {
    const n = parseFloat(s.slice(2));
    return isFinite(n) ? n * 100 : fallback;
  }
  const n = parseFloat(s.replace(/,/g, ''));
  return isFinite(n) ? n : fallback;
}

function formatAltRange(low: number, high: number): string {
  const lo = low === 0 ? 'SFC' : `${low.toLocaleString()} ft MSL`;
  const hi = high >= 99_000 ? 'unlimited' : `${high.toLocaleString()} ft MSL`;
  return `${lo} – ${hi}`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ConflictCheckInput {
  /** Route waypoints as [lon, lat] pairs. Must have ≥ 2 entries. */
  routeCoords: [number, number][];
  /** Planned cruise altitude in feet MSL. */
  cruiseAltFt: number;
  /** Active TFR features from /api/tfr or /api/fire/tfrs. */
  tfrFeatures: GeoJSON.Feature[];
}

/**
 * Check a flight-plan route against a list of TFR GeoJSON features.
 * Returns an array of conflicts (one entry per conflicting TFR).
 * Returns empty array if no conflicts detected.
 */
export function checkRouteVsTfrs({
  routeCoords,
  cruiseAltFt,
  tfrFeatures,
}: ConflictCheckInput): TfrConflict[] {
  if (routeCoords.length < 2) return [];

  const conflicts: TfrConflict[] = [];

  for (const feature of tfrFeatures) {
    const geom = feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
    if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) continue;

    const props = (feature.properties ?? {}) as Record<string, unknown>;

    // Altitude check — WFS fields are LOW_ALTITUDE / HI_ALTITUDE (numeric,
    // feet MSL). Absent → conservatively include.
    const lowAlt = parseAltFt(props.LOW_ALTITUDE ?? props.LOW_ALT, 0);
    const hiAlt  = parseAltFt(props.HI_ALTITUDE  ?? props.HI_ALT,  99_999);

    if (cruiseAltFt < lowAlt || cruiseAltFt > hiAlt) continue;

    // Geometric intersection — walk each route leg.
    let entryLegIndex = -1;
    for (let i = 0; i < routeCoords.length - 1; i++) {
      const [ax, ay] = routeCoords[i];
      const [bx, by] = routeCoords[i + 1];
      const hit =
        geom.type === 'Polygon'
          ? segmentIntersectsPolygon(ax, ay, bx, by, geom)
          : segmentIntersectsMultiPolygon(ax, ay, bx, by, geom);
      if (hit) {
        entryLegIndex = i;
        break;
      }
    }

    if (entryLegIndex < 0) continue;

    const notamId =
      typeof props.NOTAM_KEY === 'string'
        ? props.NOTAM_KEY.split('-')[0]  // e.g. '6/0092' from '6/0092-1-FDC-F'
        : (typeof props.notam_id === 'string' ? props.notam_id : 'UNKNOWN');

    const description =
      typeof props.tfr_description === 'string'
        ? props.tfr_description
        : typeof props.description === 'string'
          ? props.description
          : '';

    conflicts.push({
      notamId,
      altitudeRange: formatAltRange(lowAlt, hiAlt),
      description,
      entryLegIndex,
      listUrl:
        typeof props.tfr_list_url === 'string'
          ? props.tfr_list_url
          : 'https://tfr.faa.gov/tfr3/',
    });
  }

  return conflicts;
}
